import { memo, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { kanal, type Datei } from '@beholder/shared';
import {
  WB_FARBEN,
  WB_FUELLUNGEN,
  WB_ARTEN,
  WB_NOTIZ_FARBEN,
  wbBenutzerFarbe,
  wbElementSvg,
  wbHuelle,
  wbHuelleAlle,
  wbSortiert,
  wbStandard,
  wbBeschriftungBreite,
  wbTextKasten,
  wbZeilen,
  type WbAenderung,
  type WbAnwesend,
  type WbArt,
  type WbElement,
  type WbFelder,
  type WbFluechtig,
  type WbTafelFarbe,
  WB_TAFELFARBEN,
  WB_TAFELFARBE_NAMEN,
  type WbVomServer,
} from '@beholder/shared/module/whiteboard';
import type { ModulSeiteProps } from '../../core/modul';
import { api, modulPfad } from '../../core/api';
import { live, useFluechtig, useLive, useLiveVerbunden } from '../../core/live';
import { Symbol } from '../../core/symbole';
import { Auswahl, Knopf, Leer, Meldung } from '../../core/ui';
import { TafelSync, neueId } from './sync';
import { alsPngSpeichern, alsSvgSpeichern, bildAdresse } from './export';
import {
  ebeneSchritt,
  endpunkteWelt,
  halterUnter,
  pfeilLage,
  pfeileNachziehen,
  absolutePunkte,
  auswahlKasten,
  drehen,
  drehenUm,
  kastenIn,
  kastenPunkt,
  punkteNormieren,
  skalieren,
  vereinfachen,
  type Ansicht,
  type DrehKasten,
  type Punkt,
} from './geometrie';

type Werkzeug = 'auswahl' | 'hand' | 'stift' | 'linie' | 'pfeil' | 'rechteck' | 'ellipse' | 'raute' | 'text' | 'notiz' | 'radierer';

interface Stil {
  farbe: string;
  fuellung: string | null;
  staerke: number;
  schrift: number;
  notizFarbe: string;
}

interface Fremd {
  verbindungId: string;
  name: string;
  farbe: string;
  x: number;
  y: number;
  entwurf: WbElement | null;
  zeit: number;
}

interface Editor {
  id: string;
  neu: boolean;
  art: 'text' | 'notiz' | 'form';
  text: string;
  vorher: WbFelder;
}

type Lage = Pick<WbElement, 'x' | 'y' | 'b' | 'h' | 'drehung' | 'punkte' | 'schrift' | 'art' | 'text' | 'start' | 'ende'>;

type Zug =
  | { art: 'pan'; s0: Punkt; v0: Ansicht }
  | { art: 'stift'; punkte: number[]; letzter: Punkt }
  | { art: 'form'; start: Punkt; werkzeug: Werkzeug }
  | { art: 'verschieben'; start: Punkt; orig: Map<string, Lage>; pfeile: Map<string, WbFelder>; gesendet: number; bewegt: boolean }
  | { art: 'skalieren'; sx: number; sy: number; k0: DrehKasten; orig: Map<string, Lage>; pfeile: Map<string, WbFelder>; gesendet: number }
  | { art: 'drehen'; k0: DrehKasten; w0: number; orig: Map<string, Lage>; pfeile: Map<string, WbFelder>; gesendet: number }
  | { art: 'endpunkt'; id: string; welcher: 0 | 1; andere: Punkt; orig: WbElement; gesendet: number }
  | { art: 'rahmen'; start: Punkt; vorher: Set<string> }
  | { art: 'radieren' }
  | { art: 'pinch'; d0: number; m0: Punkt; v0: Ansicht };

const WERKZEUGE: { id: Werkzeug; name: string; taste: string; symbol: ReactNode }[] = [
  { id: 'auswahl', name: 'Auswählen', taste: 'V', symbol: <path d="M5 3l14 8-6 1.5L10 19z" /> },
  { id: 'hand', name: 'Fläche verschieben', taste: 'H', symbol: <path d="M8 12V5.5a1.5 1.5 0 0 1 3 0V11M11 10V4.5a1.5 1.5 0 0 1 3 0V11M14 10.5V6a1.5 1.5 0 0 1 3 0v8c0 4-2.5 7-6.5 7-3 0-4.5-1.5-6-4l-2-3.5a1.5 1.5 0 0 1 2.5-1.6L8 14" /> },
  { id: 'stift', name: 'Stift', taste: 'P', symbol: <path d="M4 20l1-5L16 4l4 4L9 19zM14 6l4 4" /> },
  { id: 'linie', name: 'Linie', taste: 'L', symbol: <path d="M5 19L19 5" /> },
  { id: 'pfeil', name: 'Pfeil', taste: 'A', symbol: <path d="M5 19L19 5M10 5h9v9" /> },
  { id: 'rechteck', name: 'Rechteck', taste: 'R', symbol: <rect x="4" y="6" width="16" height="12" rx="2" /> },
  { id: 'ellipse', name: 'Ellipse', taste: 'O', symbol: <ellipse cx="12" cy="12" rx="8.5" ry="6.5" /> },
  { id: 'raute', name: 'Raute', taste: 'D', symbol: <path d="M12 3l9 9-9 9-9-9z" /> },
  { id: 'text', name: 'Text', taste: 'T', symbol: <path d="M5 6V4h14v2M12 4v16M9 20h6" /> },
  { id: 'notiz', name: 'Haftnotiz', taste: 'N', symbol: <path d="M4 4h16v11l-5 5H4zM15 20v-5h5" /> },
  { id: 'radierer', name: 'Radierer', taste: 'E', symbol: <path d="M8 20h12M4.5 14.5l9-9a2 2 0 0 1 2.8 0l2.2 2.2a2 2 0 0 1 0 2.8L11 18H8z" /> },
];

const TASTEN: Record<string, Werkzeug> = Object.fromEntries(WERKZEUGE.map((w) => [w.taste.toLowerCase(), w.id]));
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 8;
const SENDEN_ALLE = 70; // ms zwischen Zwischenständen beim Ziehen

const Sym = ({ children }: { children: ReactNode }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {children}
  </svg>
);

const ElementG = memo(function ElementG({ e, blass, geist, versteckt }: { e: WbElement; blass?: boolean; geist?: boolean; versteckt?: boolean }) {
  const html = useMemo(() => wbElementSvg(e, { treffer: !geist, bildAdresse }), [e, geist]);
  return (
    <g
      data-id={geist ? undefined : e.id}
      data-art={e.art}
      opacity={versteckt ? 0 : blass ? 0.3 : geist ? 0.55 : undefined}
      pointerEvents={geist ? 'none' : undefined}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

function istFlaeche(art: WbArt) {
  return art === 'rechteck' || art === 'ellipse' || art === 'raute';
}
function hatStrich(art: WbArt) {
  return art === 'stift' || art === 'linie' || art === 'pfeil' || istFlaeche(art);
}

/** Eingabefeld für die Beschriftung einer Form: mittig, so breit wie der Textbereich der Form. */
function formEditorStil(e: WbElement, text: string, lage: Punkt, zoom: number): React.CSSProperties {
  const innen = wbBeschriftungBreite(e.art, e.b);
  const zeilen = Math.max(1, wbZeilen(text || ' ', e.schrift, innen).length);
  const hoehe = zeilen * e.schrift * 1.25;
  const links = (e.b - innen) / 2;
  const oben = Math.max(0, (e.h - hoehe) / 2);
  return {
    left: lage.x + links * zoom,
    top: lage.y + oben * zoom,
    width: innen * zoom,
    height: (hoehe + 4) * zoom,
    fontSize: e.schrift * zoom,
    lineHeight: 1.25,
    padding: 0,
    textAlign: 'center',
    color: tinte(e.farbe),
    background: 'transparent',
    transform: e.drehung ? `rotate(${e.drehung}deg)` : undefined,
    transformOrigin: `${(e.b / 2 - links) * zoom}px ${(e.h / 2 - oben) * zoom}px`,
  };
}

/** Schwarze/weiße Tinte folgt dem Hintergrund der Tafel (siehe stil.css). */
function tinte(f: string) {
  return f === '#1e1e1e' ? 'var(--wb-tinte)' : f === '#ffffff' ? 'var(--wb-papier)' : f;
}

const ENTWURF_GRENZE = 3500; // flüchtige Nachrichten dürfen höchstens 4 KB groß sein

/** Entwurf für die Anderen verkleinern: nur nötige Felder, lange Striche ausgedünnt. */
function entwurfKlein(e: WbElement): Partial<WbElement> {
  const klein: Partial<WbElement> = {
    art: e.art, x: e.x, y: e.y, b: e.b, h: e.h, drehung: e.drehung, farbe: e.farbe, fuellung: e.fuellung, staerke: e.staerke,
    punkte: e.punkte ? e.punkte.map((v) => Math.round(v)) : null,
  };
  let p = klein.punkte;
  while (p && JSON.stringify(klein).length > ENTWURF_GRENZE && p.length > 8) {
    p = p.filter((_, i) => i % 4 < 2 || i >= p!.length - 2);
    klein.punkte = p;
  }
  return JSON.stringify(klein).length > ENTWURF_GRENZE ? { ...klein, punkte: p ? [p[0]!, p[1]!, p[p.length - 2]!, p[p.length - 1]!] : null } : klein;
}

const zahlOk = (v: unknown, max = 1e7) => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= max;

/** Entwurf von anderen prüfen, bevor er gezeichnet wird (fremde Daten). */
function entwurfPruefen(roh: unknown): WbElement | null {
  if (!roh || typeof roh !== 'object') return null;
  const e = roh as Record<string, unknown>;
  if (!(WB_ARTEN as readonly unknown[]).includes(e.art) || e.art === 'bild') return null;
  if (!['x', 'y', 'b', 'h'].every((k) => zahlOk(e[k]))) return null;
  const punkte = Array.isArray(e.punkte) && e.punkte.length <= 4000 && e.punkte.every((v) => zahlOk(v)) ? (e.punkte as number[]) : null;
  const farbe = (v: unknown) => (typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v) ? v : null);
  return {
    ...wbStandard(e.art as WbArt, 'entwurf'),
    x: e.x as number,
    y: e.y as number,
    b: e.b as number,
    h: e.h as number,
    drehung: zahlOk(e.drehung, 3600) ? (e.drehung as number) : 0,
    staerke: zahlOk(e.staerke, 100) ? Math.max(0.5, e.staerke as number) : 3,
    farbe: farbe(e.farbe) ?? '#1e1e1e',
    fuellung: farbe(e.fuellung),
    punkte,
  };
}

export function TafelSeite(p: ModulSeiteProps) {
  const { tafelId = '' } = useParams();
  return <Brett key={tafelId} {...p} tafelId={tafelId} />;
}

function Brett(p: ModulSeiteProps & { tafelId: string }) {
  const { projekt, darfSchreiben, tafelId } = p;
  const kanalName = kanal(projekt.id, 'whiteboard', tafelId);
  // Brett wird je Tafel neu aufgebaut (key), daher bleibt der Abgleich für die ganze Lebensdauer derselbe
  const [sync] = useState(() => new TafelSync((d) => live.senden(kanalName, d)));
  const stand = useSyncExternalStore(sync.abo, () => sync.stand);
  const verbunden = useLiveVerbunden();

  const [name, setName] = useState('');
  const [tafelFarbe, setTafelFarbe] = useState<WbTafelFarbe>('design');
  const [weg, setWeg] = useState(false);
  const [anwesende, setAnwesende] = useState<WbAnwesend[]>([]);
  const [fremde, setFremde] = useState<Map<string, Fremd>>(new Map());
  const [meldung, setMeldung] = useState('');
  const [ansicht, setAnsichtRoh] = useState<Ansicht>({ x: -400, y: -250, zoom: 1 });
  const ansichtRef = useRef(ansicht);
  const setAnsicht = useCallback((v: Ansicht) => {
    ansichtRef.current = v;
    setAnsichtRoh(v);
  }, []);
  const [werkzeug, setWerkzeugRoh] = useState<Werkzeug>(darfSchreiben ? 'auswahl' : 'hand');
  const [stil, setStil] = useState<Stil>({ farbe: WB_FARBEN[0], fuellung: null, staerke: 3, schrift: 24, notizFarbe: WB_NOTIZ_FARBEN[0] });
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [vorschau, setVorschau] = useState<Map<string, WbFelder> | null>(null);
  /** Element, an dem der gerade gezogene Pfeil haften wird (Hervorhebung). */
  const [halter, setHalter] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<WbElement | null>(null);
  const [rahmen, setRahmen] = useState<{ x: number; y: number; b: number; h: number } | null>(null);
  const [radiert, setRadiert] = useState<Set<string>>(new Set());
  const [editor, setEditorRoh] = useState<Editor | null>(null);
  const editorAktiv = useRef<Editor | null>(null);
  const setEditor = (e: Editor | null) => {
    editorAktiv.current = e;
    setEditorRoh(e);
  };
  const [exportLaeuft, setExportLaeuft] = useState(false);
  const [nameBearbeiten, setNameBearbeiten] = useState<string | null>(null);
  const [groesse, setGroesse] = useState({ b: 800, h: 600 });

  const rahmenRef = useRef<HTMLDivElement>(null);
  const zugRef = useRef<Zug | null>(null);
  const zeigerRef = useRef(new Map<number, Punkt>());
  const leertaste = useRef(false);
  const zeigerGesendet = useRef(0);
  const ersteAnsicht = useRef(false);
  const ablage = useRef<WbElement[]>([]);
  const einfuegenZahl = useRef(0);
  const dateiEingabe = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const editorGesendet = useRef(0);

  const setWerkzeug = (w: Werkzeug) => {
    setWerkzeugRoh(darfSchreiben ? w : 'hand');
    if (w !== 'auswahl') setAuswahl(new Set());
  };
  useEffect(() => {
    if (!darfSchreiben) {
      setWerkzeugRoh('hand');
      setAuswahl(new Set());
      setEditor(null);
    }
  }, [darfSchreiben]);

  // ---------------------------------------------------------------- Daten

  const alle = sync.ansicht();
  const sichtbar = useMemo(() => wbSortiert(alle.values()), [stand]); // eslint-disable-line react-hooks/exhaustive-deps
  const anzeige = useCallback(
    (id: string): WbElement | undefined => {
      const e = sync.get(id);
      const v = vorschau?.get(id);
      return e && v ? { ...e, ...v } : e;
    },
    [sync, vorschau, stand], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const auswahlEls = useMemo(() => [...auswahl].map(anzeige).filter((e): e is WbElement => !!e), [auswahl, anzeige]);

  const allesZeigen = useCallback(
    (els: WbElement[] = wbSortiert(sync.ansicht().values())) => {
      const k = wbHuelleAlle(els);
      const r = rahmenRef.current?.getBoundingClientRect();
      const b = r?.width || 800;
      const h = r?.height || 600;
      if (!k) {
        setAnsicht({ x: -b / 2, y: -h / 2, zoom: 1 });
        return;
      }
      const rand = 60;
      const zoom = Math.min(2, Math.max(ZOOM_MIN, Math.min((b - 2 * rand) / Math.max(1, k.b), (h - 2 * rand) / Math.max(1, k.h))));
      setAnsicht({ x: k.x + k.b / 2 - b / 2 / zoom, y: k.y + k.h / 2 - h / 2 / zoom, zoom });
    },
    [sync, setAnsicht],
  );

  useLive(kanalName, (roh) => {
    const d = roh as WbVomServer;
    switch (d.typ) {
      case 'zustand':
        sync.empfangen(d);
        setName(d.tafel.name);
        setTafelFarbe(d.tafel.farbe ?? 'design');
        setAnwesende(d.anwesende);
        if (!ersteAnsicht.current) {
          ersteAnsicht.current = true;
          allesZeigen();
        }
        break;
      case 'geaendert':
      case 'abgelehnt': {
        const text = sync.empfangen(d);
        if (text) setMeldung(text);
        break;
      }
      case 'zeiger-weg':
        setFremde((m) => {
          if (!m.has(d.verbindungId)) return m;
          const n = new Map(m);
          n.delete(d.verbindungId);
          return n;
        });
        break;
      case 'anwesende':
        setAnwesende(d.liste);
        setFremde((m) => new Map([...m].filter(([id]) => d.liste.some((a) => a.verbindungId === id))));
        break;
      case 'umbenannt':
        setName(d.name);
        break;
      case 'farbe':
        setTafelFarbe(d.farbe);
        break;
      case 'geloescht':
        setWeg(true);
        break;
    }
  });

  // Zeiger der anderen (auch von Leuten, die nur ansehen) – flüchtig, nichts wird gespeichert
  useFluechtig(kanalName, (roh, von) => {
    const d = roh as WbFluechtig;
    if (d?.typ === 'zeiger-weg') {
      setFremde((m) => {
        if (!m.has(von.verbindungId)) return m;
        const n = new Map(m);
        n.delete(von.verbindungId);
        return n;
      });
      return;
    }
    if (d?.typ !== 'zeiger' || !Number.isFinite(d.x) || !Number.isFinite(d.y)) return;
    setFremde((m) => {
      const n = new Map(m);
      n.set(von.verbindungId, {
        verbindungId: von.verbindungId,
        name: von.anzeigename,
        farbe: wbBenutzerFarbe(von.benutzerId),
        x: d.x,
        y: d.y,
        entwurf: entwurfPruefen(d.entwurf),
        zeit: Date.now(),
      });
      return n;
    });
  });

  // Zeiger, die lange still stehen, ausblenden
  useEffect(() => {
    const t = setInterval(() => {
      setFremde((m) => {
        const grenze = Date.now() - 30_000;
        if (![...m.values()].some((f) => f.zeit < grenze)) return m;
        return new Map([...m].filter(([, f]) => f.zeit >= grenze));
      });
    }, 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const el = rahmenRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => e && setGroesse({ b: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [weg]);

  // ---------------------------------------------------------------- Hilfen

  const bildschirm = (e: { clientX: number; clientY: number }): Punkt => {
    const r = rahmenRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const welt = (s: Punkt): Punkt => {
    const v = ansichtRef.current;
    return { x: s.x / v.zoom + v.x, y: s.y / v.zoom + v.y };
  };
  const aufSchirm = (w: Punkt): Punkt => {
    const v = ansichtRef.current;
    return { x: (w.x - v.x) * v.zoom, y: (w.y - v.y) * v.zoom };
  };

  const naechstesZ = () => {
    let m = 0;
    for (const e of sync.ansicht().values()) if (e.z > m) m = e.z;
    return m + 1;
  };

  /** Gruppenmitglieder dazunehmen. */
  const mitGruppe = (ids: Iterable<string>): Set<string> => {
    const aus = new Set<string>();
    const gruppen = new Set<string>();
    for (const id of ids) {
      const e = sync.get(id);
      if (!e) continue;
      aus.add(id);
      if (e.gruppe) gruppen.add(e.gruppe);
    }
    if (gruppen.size) for (const e of sichtbar) if (e.gruppe && gruppen.has(e.gruppe)) aus.add(e.id);
    return aus;
  };

  const lagen = (ids: Iterable<string>) => {
    const m = new Map<string, Lage>();
    for (const id of ids) {
      const e = sync.get(id);
      if (e) m.set(id, { x: e.x, y: e.y, b: e.b, h: e.h, drehung: e.drehung, punkte: e.punkte, schrift: e.schrift, art: e.art, text: e.text, start: e.start, ende: e.ende });
    }
    return m;
  };

  const istLinie = (e: WbElement | undefined) => e?.art === 'linie' || e?.art === 'pfeil';

  /** Ausgangslage der Pfeile, die an den bewegten Elementen haften (für Rückgängig). */
  const pfeilOrig = (ids: Set<string>) => {
    const m = new Map<string, WbFelder>();
    for (const p of sichtbar) {
      if (!istLinie(p) || ids.has(p.id)) continue;
      if ((p.start && ids.has(p.start)) || (p.ende && ids.has(p.ende))) {
        m.set(p.id, { x: p.x, y: p.y, b: p.b, h: p.h, punkte: p.punkte, drehung: p.drehung });
      }
    }
    return m;
  };

  /** Haftende Pfeile mitziehen; mitbewegte Pfeile lösen sich von Elementen, die nicht mitwandern. */
  const mitPfeilen = (m: Map<string, WbFelder>, bewegt: Set<string>) => {
    for (const id of bewegt) {
      const p = sync.get(id);
      const f = m.get(id);
      if (!f || !p || !istLinie(p)) continue;
      if (p.start && !bewegt.has(p.start)) f.start = null;
      if (p.ende && !bewegt.has(p.ende)) f.ende = null;
    }
    for (const [id, f] of pfeileNachziehen(sichtbar, m, (x) => sync.get(x))) m.set(id, f);
    return m;
  };

  const vorherAus = (orig: Map<string, object>, nachher: Map<string, WbFelder>): WbAenderung[] =>
    [...nachher].map(([id, f]) => {
      const o = orig.get(id) as Record<string, unknown> | undefined;
      const v: Record<string, unknown> = {};
      for (const k of Object.keys(f)) v[k] = o?.[k];
      return { id, felder: v as WbFelder };
    });

  const alsAenderungen = (m: Map<string, WbFelder>): WbAenderung[] => [...m].map(([id, felder]) => ({ id, felder }));

  const zeigerSenden = (w: Punkt | null, mitEntwurf?: WbElement | null, immer = false) => {
    if (!verbunden) return;
    const jetzt = Date.now();
    if (w && !immer && jetzt - zeigerGesendet.current < 50) return;
    zeigerGesendet.current = jetzt;
    const n: WbFluechtig = w ? { typ: 'zeiger', x: Math.round(w.x), y: Math.round(w.y), entwurf: mitEntwurf ? entwurfKlein(mitEntwurf) : null } : { typ: 'zeiger-weg' };
    live.fluechtig(kanalName, n);
  };

  const zoomUm = (faktor: number, s: Punkt = { x: groesse.b / 2, y: groesse.h / 2 }) => {
    const v = ansichtRef.current;
    const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.zoom * faktor));
    const w = { x: s.x / v.zoom + v.x, y: s.y / v.zoom + v.y };
    setAnsicht({ x: w.x - s.x / zoom, y: w.y - s.y / zoom, zoom });
  };

  // Mausrad: Zoomen (Maus) bzw. Verschieben (Touchpad), Strg/Pinch zoomt immer
  useEffect(() => {
    const el = rahmenRef.current;
    if (!el) return;
    const rad = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const s = { x: e.clientX - r.left, y: e.clientY - r.top };
      const maus = e.deltaMode !== 0 || (e.deltaX === 0 && Math.abs(e.deltaY) >= 50 && Number.isInteger(e.deltaY));
      if (e.ctrlKey || e.metaKey || (maus && !e.shiftKey)) {
        const v = ansichtRef.current;
        const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.zoom * Math.exp(-e.deltaY * (e.ctrlKey && !maus ? 0.01 : 0.0015))));
        const w = { x: s.x / v.zoom + v.x, y: s.y / v.zoom + v.y };
        setAnsicht({ x: w.x - s.x / zoom, y: w.y - s.y / zoom, zoom });
      } else {
        const v = ansichtRef.current;
        const dx = e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX;
        const dy = e.shiftKey && !e.deltaX ? 0 : e.deltaY;
        setAnsicht({ ...v, x: v.x + dx / v.zoom, y: v.y + dy / v.zoom });
      }
    };
    el.addEventListener('wheel', rad, { passive: false });
    return () => el.removeEventListener('wheel', rad);
  }, [setAnsicht, weg]);

  // ---------------------------------------------------------------- Aktionen

  const loeschen = (ids: Iterable<string>) => {
    const liste = [...ids].filter((id) => sync.get(id));
    if (!liste.length) return;
    sync.aendern(liste.map((id) => ({ id, felder: { geloescht: true } })));
    setAuswahl(new Set());
  };

  const stilSetzen = (f: Partial<Stil>) => {
    const neu = { ...stil, ...f };
    setStil(neu);
    if (!auswahlEls.length || !darfSchreiben) return;
    const aend: WbAenderung[] = [];
    for (const e of auswahlEls) {
      const felder: WbFelder = {};
      if (f.farbe !== undefined) felder.farbe = f.farbe;
      if (f.fuellung !== undefined && istFlaeche(e.art)) felder.fuellung = f.fuellung;
      if (f.notizFarbe !== undefined && e.art === 'notiz') felder.fuellung = f.notizFarbe;
      if (f.staerke !== undefined && hatStrich(e.art)) felder.staerke = f.staerke;
      if (f.schrift !== undefined && (e.art === 'text' || e.art === 'notiz' || istFlaeche(e.art))) {
        felder.schrift = f.schrift;
        if (e.art === 'text') Object.assign(felder, wbTextKasten(e.text, f.schrift));
      }
      if (Object.keys(felder).length) aend.push({ id: e.id, felder });
    }
    sync.aendern(aend);
  };

  const ebene = (richtung: 'vorn' | 'hinten') => {
    if (!auswahlEls.length) return;
    const sortiert = [...auswahlEls].sort((a, b) => a.z - b.z);
    const werte = sichtbar.map((e) => e.z);
    const max = Math.max(...werte);
    const min = Math.min(...werte);
    sync.aendern(
      sortiert.map((e, i) => ({ id: e.id, felder: { z: richtung === 'vorn' ? max + 1 + i : min - sortiert.length + i } })),
    );
  };

  const ebeneEinzeln = (richtung: 1 | -1) => {
    if (!auswahlEls.length) return;
    const m = ebeneSchritt(sichtbar, new Set(auswahlEls.map((e) => e.id)), richtung);
    sync.aendern([...m].map(([id, z]) => ({ id, felder: { z } })));
  };

  const gruppieren = (an: boolean) => {
    if (!auswahlEls.length) return;
    const gid = an ? neueId() : null;
    sync.aendern(auswahlEls.map((e) => ({ id: e.id, felder: { gruppe: gid } })));
  };

  const kopieren = () => {
    ablage.current = auswahlEls.map((e) => ({ ...e }));
    einfuegenZahl.current = 0;
  };

  const einfuegen = (els = ablage.current, versatz?: Punkt) => {
    if (!els.length || !darfSchreiben) return;
    einfuegenZahl.current++;
    const d = versatz ?? { x: 24 * einfuegenZahl.current, y: 24 * einfuegenZahl.current };
    const gruppen = new Map<string, string>();
    const neueIds = new Map(els.map((e) => [e.id, neueId()]));
    let z = naechstesZ();
    const aend: WbAenderung[] = [...els]
      .sort((a, b) => a.z - b.z)
      .map((e) => {
        const { id: _id, version: _v, ...felder } = e;
        if (e.gruppe && !gruppen.has(e.gruppe)) gruppen.set(e.gruppe, neueId());
        return {
          id: neueIds.get(e.id)!,
          felder: {
            ...felder,
            x: e.x + d.x,
            y: e.y + d.y,
            z: z++,
            gruppe: e.gruppe ? gruppen.get(e.gruppe)! : null,
            start: e.start ? (neueIds.get(e.start) ?? null) : null,
            ende: e.ende ? (neueIds.get(e.ende) ?? null) : null,
            geloescht: false,
          },
        };
      });
    sync.aendern(aend);
    setWerkzeugRoh('auswahl');
    setAuswahl(new Set(aend.map((a) => a.id)));
  };

  const bildEinfuegen = async (datei: File, an?: Punkt) => {
    if (!darfSchreiben) return;
    if (!/^image\/(png|jpeg|gif|webp)$/.test(datei.type)) {
      setMeldung('Bitte ein Bild im Format PNG, JPG, GIF oder WebP wählen.');
      return;
    }
    try {
      const masse = await new Promise<{ b: number; h: number }>((ok) => {
        const url = URL.createObjectURL(datei);
        const bild = new Image();
        bild.onload = () => {
          ok({ b: bild.naturalWidth || 400, h: bild.naturalHeight || 300 });
          URL.revokeObjectURL(url);
        };
        bild.onerror = () => {
          ok({ b: 400, h: 300 });
          URL.revokeObjectURL(url);
        };
        bild.src = url;
      });
      const [d] = await api.hochladen<Datei[]>(`/api/projekte/${projekt.id}/dateien?bereich=whiteboard&bezug=${tafelId}`, [datei]);
      if (!d) return;
      const v = ansichtRef.current;
      const f = Math.min(1, 480 / masse.b, 480 / masse.h) / Math.max(0.25, v.zoom) * Math.min(1, v.zoom);
      const b = masse.b * f;
      const h = masse.h * f;
      const m = an ?? welt({ x: groesse.b / 2, y: groesse.h / 2 });
      const id = neueId();
      sync.aendern([{ id, felder: { ...wbStandard('bild', id), x: m.x - b / 2, y: m.y - h / 2, b, h, dateiId: d.id, z: naechstesZ() } }]);
      setWerkzeugRoh('auswahl');
      setAuswahl(new Set([id]));
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : 'Das Bild konnte nicht hochgeladen werden.');
    }
  };

  // ---------------------------------------------------------------- Text bearbeiten

  const editorOeffnen = (e: WbElement, neu: boolean) => {
    if (!darfSchreiben || (e.art !== 'text' && e.art !== 'notiz' && !istFlaeche(e.art))) return;
    if (neu) sync.lokal([{ id: e.id, felder: { ...e, geloescht: false } }]);
    const art = e.art === 'text' || e.art === 'notiz' ? e.art : 'form';
    setEditor({ id: e.id, neu, art, text: e.text, vorher: neu ? { geloescht: true } : { text: e.text, b: e.b, h: e.h } });
    setAuswahl(new Set());
    editorGesendet.current = 0;
  };

  const editorFelder = (ed: Editor, text: string): WbFelder => {
    const e = sync.get(ed.id);
    if (!e) return { text };
    if (ed.art === 'text') return { text, ...wbTextKasten(text, e.schrift) };
    if (ed.art === 'form') {
      const noetigForm = wbZeilen(text, e.schrift, wbBeschriftungBreite(e.art, e.b)).length * e.schrift * 1.25 + 16;
      return { text, h: e.art === 'rechteck' ? Math.max(e.h, noetigForm) : e.h };
    }
    const noetig = wbZeilen(text, e.schrift, Math.max(10, e.b - 24)).length * e.schrift * 1.25 + 24;
    return { text, h: Math.max(e.h, noetig) };
  };

  const editorTippen = (text: string) => {
    const editor = editorAktiv.current;
    if (!editor) return;
    setEditor({ ...editor, text });
    const jetzt = Date.now();
    if (jetzt - editorGesendet.current > 300) {
      editorGesendet.current = jetzt;
      sync.lokal([{ id: editor.id, felder: editorFelder(editor, text) }]);
    }
  };

  const editorFertig = () => {
    const ed = editorAktiv.current;
    if (!ed) return;
    setEditor(null);
    const e = sync.get(ed.id);
    if (!e) return;
    const leer = !ed.text.trim();
    if (leer && ed.art === 'text') {
      // Leerer Text verschwindet wieder
      sync.lokal([{ id: ed.id, felder: { geloescht: true } }]);
      if (!ed.neu) sync.aendern([{ id: ed.id, felder: { geloescht: true } }], [{ id: ed.id, felder: { geloescht: false, ...ed.vorher } }]);
      return;
    }
    const felder = editorFelder(ed, ed.text);
    if (ed.neu) sync.aendern([{ id: ed.id, felder: { ...e, ...felder, geloescht: false } }], [{ id: ed.id, felder: { geloescht: true } }]);
    else sync.aendern([{ id: ed.id, felder }], [{ id: ed.id, felder: ed.vorher }]);
    if (werkzeug === 'text' || werkzeug === 'notiz') setWerkzeugRoh('auswahl');
    setAuswahl(new Set([ed.id]));
  };

  // ---------------------------------------------------------------- Zeiger (Maus, Stift, Finger)

  const elementUnter = (t: EventTarget | null): string | null => {
    const el = (t as Element | null)?.closest?.('[data-id]');
    return el?.getAttribute('data-id') ?? null;
  };

  const zugAbbrechen = () => {
    const z = zugRef.current;
    zugRef.current = null;
    setEntwurf(null);
    setRahmen(null);
    setRadiert(new Set());
    if (z && (z.art === 'verschieben' || z.art === 'skalieren' || z.art === 'drehen')) {
      // Zwischenstände zurücknehmen
      sync.lokal(alsAenderungen(new Map([...z.orig].map(([id, l]) => [id, { x: l.x, y: l.y, b: l.b, h: l.h, drehung: l.drehung, punkte: l.punkte, schrift: l.schrift, start: l.start, ende: l.ende }]))));
      sync.lokal(alsAenderungen(z.pfeile));
    }
    if (z?.art === 'endpunkt') {
      const o = z.orig;
      sync.lokal([{ id: o.id, felder: { x: o.x, y: o.y, b: o.b, h: o.h, punkte: o.punkte, drehung: o.drehung, start: o.start, ende: o.ende } }]);
    }
    setVorschau(null);
    setHalter(null);
  };

  const unten = (e: React.PointerEvent) => {
    if (editor) {
      editorFertig();
      return;
    }
    const s = bildschirm(e);
    zeigerRef.current.set(e.pointerId, s);
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      /* Zeiger schon weg */
    }

    // Zwei Finger: zoomen und verschieben (bricht alles andere ab)
    if (zeigerRef.current.size === 2) {
      zugAbbrechen();
      const [a, b] = [...zeigerRef.current.values()] as [Punkt, Punkt];
      zugRef.current = { art: 'pinch', d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, m0: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, v0: ansichtRef.current };
      return;
    }
    if (zeigerRef.current.size > 2) return;

    const w = welt(s);
    if (e.button === 1 || e.button === 2 || werkzeug === 'hand' || leertaste.current || !darfSchreiben) {
      if (e.button === 2) return;
      zugRef.current = { art: 'pan', s0: s, v0: ansichtRef.current };
      return;
    }
    if (e.button !== 0) return;

    switch (werkzeug) {
      case 'stift': {
        zugRef.current = { art: 'stift', punkte: [w.x, w.y], letzter: w };
        setEntwurf({ ...wbStandard('stift', 'entwurf'), farbe: stil.farbe, staerke: stil.staerke, x: 0, y: 0, punkte: [w.x, w.y] });
        return;
      }
      case 'linie':
      case 'pfeil':
      case 'rechteck':
      case 'ellipse':
      case 'raute':
        zugRef.current = { art: 'form', start: w, werkzeug };
        return;
      case 'text': {
        const id = elementUnter(e.target);
        const da = id ? sync.get(id) : undefined;
        if (da && (da.art === 'text' || da.art === 'notiz' || istFlaeche(da.art))) return editorOeffnen(da, false);
        const neuId = neueId();
        editorOeffnen({ ...wbStandard('text', neuId), x: w.x, y: w.y - stil.schrift * 0.6, farbe: stil.farbe, schrift: stil.schrift, ...wbTextKasten('', stil.schrift), z: naechstesZ() }, true);
        return;
      }
      case 'notiz': {
        const neuId = neueId();
        editorOeffnen({ ...wbStandard('notiz', neuId), x: w.x - 100, y: w.y - 80, fuellung: stil.notizFarbe, z: naechstesZ() }, true);
        return;
      }
      case 'radierer': {
        zugRef.current = { art: 'radieren' };
        const id = elementUnter(e.target);
        setRadiert(id ? new Set([id]) : new Set());
        return;
      }
      case 'auswahl': {
        const griff = (e.target as Element).closest?.('[data-griff]')?.getAttribute('data-griff');
        if (griff && auswahlEls.length) {
          const ids = new Set(auswahlEls.map((x) => x.id));
          const k0 = auswahlKasten(auswahlEls)!;
          if (griff === 'drehen') {
            zugRef.current = { art: 'drehen', k0, w0: Math.atan2(w.y - k0.cy, w.x - k0.cx), orig: lagen(ids), pfeile: pfeilOrig(ids), gesendet: 0 };
          } else if (griff === 'p0' || griff === 'p1') {
            const el = auswahlEls[0]!;
            const ends = endpunkteWelt(el);
            zugRef.current = { art: 'endpunkt', id: el.id, welcher: griff === 'p0' ? 0 : 1, andere: griff === 'p0' ? ends[1] : ends[0], orig: el, gesendet: 0 };
          } else {
            const [sx, sy] = griff.split(',').map(Number) as [number, number];
            zugRef.current = { art: 'skalieren', sx, sy, k0, orig: lagen(ids), pfeile: pfeilOrig(ids), gesendet: 0 };
          }
          return;
        }
        const id = elementUnter(e.target);
        if (id) {
          const gruppe = mitGruppe([id]);
          let neu: Set<string>;
          if (e.shiftKey) {
            neu = new Set(auswahl);
            const drin = auswahl.has(id);
            for (const g of gruppe) drin ? neu.delete(g) : neu.add(g);
          } else neu = auswahl.has(id) ? auswahl : gruppe;
          setAuswahl(neu);
          zugRef.current = { art: 'verschieben', start: w, orig: lagen(neu), pfeile: pfeilOrig(neu), gesendet: 0, bewegt: false };
          return;
        }
        zugRef.current = { art: 'rahmen', start: w, vorher: e.shiftKey ? new Set(auswahl) : new Set() };
        if (!e.shiftKey) setAuswahl(new Set());
        return;
      }
    }
  };

  const bewegen = (e: React.PointerEvent) => {
    const s = bildschirm(e);
    if (zeigerRef.current.has(e.pointerId)) zeigerRef.current.set(e.pointerId, s);
    const w = welt(s);
    const z = zugRef.current;
    if (!z || z.art !== 'stift') zeigerSenden(w);
    if (!z) return;
    const jetzt = Date.now();

    switch (z.art) {
      case 'pinch': {
        const pts = [...zeigerRef.current.values()];
        if (pts.length < 2) return;
        const [a, b] = pts as [Punkt, Punkt];
        const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
        const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, (z.v0.zoom * d) / z.d0));
        const w0 = { x: z.m0.x / z.v0.zoom + z.v0.x, y: z.m0.y / z.v0.zoom + z.v0.y };
        setAnsicht({ x: w0.x - m.x / zoom, y: w0.y - m.y / zoom, zoom });
        return;
      }
      case 'pan':
        setAnsicht({ ...z.v0, x: z.v0.x - (s.x - z.s0.x) / z.v0.zoom, y: z.v0.y - (s.y - z.s0.y) / z.v0.zoom });
        return;
      case 'stift': {
        const abstand = Math.hypot(w.x - z.letzter.x, w.y - z.letzter.y);
        if (abstand < 0.8 / ansichtRef.current.zoom) return;
        // Glättung: der Strich folgt dem Zeiger mit leichter Verzögerung
        const g = { x: z.letzter.x + (w.x - z.letzter.x) * 0.55, y: z.letzter.y + (w.y - z.letzter.y) * 0.55 };
        z.punkte.push(g.x, g.y);
        z.letzter = g;
        const ent = { ...wbStandard('stift', 'entwurf'), farbe: stil.farbe, staerke: stil.staerke, x: 0, y: 0, punkte: [...z.punkte] };
        setEntwurf(ent);
        if (jetzt - zeigerGesendet.current > 120) zeigerSenden(w, { ...ent, ...punkteNormieren(z.punkte) }, true);
        return;
      }
      case 'form': {
        let dx = w.x - z.start.x;
        let dy = w.y - z.start.y;
        const art = z.werkzeug as WbArt;
        let ent: WbElement;
        if (art === 'linie' || art === 'pfeil') {
          if (e.shiftKey) {
            const winkel = Math.round(Math.atan2(dy, dx) / (Math.PI / 12)) * (Math.PI / 12);
            const l = Math.hypot(dx, dy);
            dx = Math.cos(winkel) * l;
            dy = Math.sin(winkel) * l;
          }
          const rand = 4 / ansichtRef.current.zoom;
          const ziel = { x: z.start.x + dx, y: z.start.y + dy };
          const hA = halterUnter(sichtbar, z.start, rand);
          const hB = halterUnter(sichtbar, ziel, rand, hA?.id);
          setHalter(hB?.id ?? null);
          ent = {
            ...wbStandard(art, 'entwurf'),
            farbe: stil.farbe,
            staerke: stil.staerke,
            ...pfeilLage(z.start, ziel, hA, hB),
            start: hA?.id ?? null,
            ende: hB?.id ?? null,
          };
        } else {
          if (e.shiftKey) {
            const m = Math.max(Math.abs(dx), Math.abs(dy));
            dx = Math.sign(dx || 1) * m;
            dy = Math.sign(dy || 1) * m;
          }
          ent = {
            ...wbStandard(art, 'entwurf'),
            farbe: stil.farbe,
            fuellung: stil.fuellung,
            staerke: stil.staerke,
            x: Math.min(z.start.x, z.start.x + dx),
            y: Math.min(z.start.y, z.start.y + dy),
            b: Math.abs(dx),
            h: Math.abs(dy),
          };
        }
        setEntwurf(ent);
        if (jetzt - zeigerGesendet.current > 100) zeigerSenden(w, ent, true);
        return;
      }
      case 'verschieben': {
        const dx = w.x - z.start.x;
        const dy = w.y - z.start.y;
        if (!z.bewegt && Math.hypot(dx, dy) * ansichtRef.current.zoom < 3) return;
        z.bewegt = true;
        const m = mitPfeilen(new Map<string, WbFelder>([...z.orig].map(([id, l]) => [id, { x: l.x + dx, y: l.y + dy }])), new Set(z.orig.keys()));
        setVorschau(m);
        if (jetzt - z.gesendet > SENDEN_ALLE) {
          z.gesendet = jetzt;
          sync.lokal(alsAenderungen(m));
        }
        return;
      }
      case 'skalieren': {
        const einzel = z.orig.size === 1 ? [...z.orig.values()][0] : null;
        const fest = e.shiftKey || einzel?.art === 'bild' || einzel?.art === 'text';
        const m = mitPfeilen(skalieren(z.k0, z.sx, z.sy, w, z.orig, fest), new Set(z.orig.keys()));
        setVorschau(m);
        if (jetzt - z.gesendet > SENDEN_ALLE) {
          z.gesendet = jetzt;
          sync.lokal(alsAenderungen(m));
        }
        return;
      }
      case 'drehen': {
        let grad = ((Math.atan2(w.y - z.k0.cy, w.x - z.k0.cx) - z.w0) * 180) / Math.PI;
        if (e.shiftKey) grad = Math.round((grad + z.k0.w) / 15) * 15 - z.k0.w;
        const m = mitPfeilen(drehenUm(z.k0, grad, z.orig), new Set(z.orig.keys()));
        setVorschau(m);
        if (jetzt - z.gesendet > SENDEN_ALLE) {
          z.gesendet = jetzt;
          sync.lokal(alsAenderungen(m));
        }
        return;
      }
      case 'endpunkt': {
        // Das gezogene Ende haftet am Element darunter, das andere bleibt, wie es war
        const h = halterUnter(sichtbar, w, 4 / ansichtRef.current.zoom, z.id);
        setHalter(h?.id ?? null);
        const andererId = z.welcher === 0 ? z.orig.ende : z.orig.start;
        const anderer = andererId ? (sync.get(andererId) ?? null) : null;
        const [startEl, endeEl] = z.welcher === 0 ? [h, anderer] : [anderer, h];
        const [a, b] = z.welcher === 0 ? [w, z.andere] : [z.andere, w];
        const m = new Map<string, WbFelder>([[z.id, { ...pfeilLage(a, b, startEl, endeEl), start: startEl?.id ?? null, ende: endeEl?.id ?? null }]]);
        setVorschau(m);
        if (jetzt - z.gesendet > SENDEN_ALLE) {
          z.gesendet = jetzt;
          sync.lokal(alsAenderungen(m));
        }
        return;
      }
      case 'rahmen': {
        const k = { x: Math.min(z.start.x, w.x), y: Math.min(z.start.y, w.y), b: Math.abs(w.x - z.start.x), h: Math.abs(w.y - z.start.y) };
        setRahmen(k);
        const drin = sichtbar.filter((x) => kastenIn(wbHuelle(x), k, true)).map((x) => x.id);
        setAuswahl(new Set([...z.vorher, ...mitGruppe(drin)]));
        return;
      }
      case 'radieren': {
        const t = document.elementFromPoint(e.clientX, e.clientY);
        const id = elementUnter(t);
        if (id && !radiert.has(id)) setRadiert((r) => new Set(r).add(id));
        return;
      }
    }
  };

  const oben = (e: React.PointerEvent) => {
    zeigerRef.current.delete(e.pointerId);
    const z = zugRef.current;
    if (!z) return;
    if (z.art === 'pinch') {
      // Ein Finger bleibt liegen: nichts weiter tun, bis er gehoben wird
      if (zeigerRef.current.size === 0) zugRef.current = null;
      return;
    }
    zugRef.current = null;
    setHalter(null);
    const w = welt(bildschirm(e));
    switch (z.art) {
      case 'stift': {
        setEntwurf(null);
        zeigerSenden(w, null, true);
        z.punkte.push(w.x, w.y);
        const p = vereinfachen(z.punkte, 0.5 / ansichtRef.current.zoom);
        const id = neueId();
        sync.aendern([{ id, felder: { ...wbStandard('stift', id), farbe: stil.farbe, staerke: stil.staerke, ...punkteNormieren(p), z: naechstesZ() } }]);
        return;
      }
      case 'form': {
        const ent = entwurf;
        setEntwurf(null);
        zeigerSenden(w, null, true);
        if (!ent || Math.max(ent.b, ent.h) * ansichtRef.current.zoom < 4) return;
        const id = neueId();
        const { id: _i, version: _v, ...felder } = ent;
        sync.aendern([{ id, felder: { ...felder, z: naechstesZ() } }]);
        return;
      }
      case 'verschieben':
      case 'skalieren':
      case 'drehen':
      case 'endpunkt': {
        const m = vorschau;
        setVorschau(null);
        if (!m || (z.art === 'verschieben' && !z.bewegt)) return;
        const orig: Map<string, object> = z.art === 'endpunkt' ? new Map([[z.id, z.orig]]) : new Map<string, object>([...z.orig, ...z.pfeile]);
        sync.aendern(alsAenderungen(m), vorherAus(orig, m));
        return;
      }
      case 'rahmen':
        setRahmen(null);
        return;
      case 'radieren': {
        const ids = radiert;
        setRadiert(new Set());
        if (ids.size) sync.aendern([...ids].map((id) => ({ id, felder: { geloescht: true } })));
        return;
      }
    }
  };

  const abgebrochen = (e: React.PointerEvent) => {
    zeigerRef.current.delete(e.pointerId);
    if (zugRef.current?.art === 'pinch') {
      if (zeigerRef.current.size === 0) zugRef.current = null;
      return;
    }
    zugAbbrechen();
  };

  const doppelklick = (e: React.MouseEvent) => {
    if (!darfSchreiben) return;
    // Wegen der Zeiger-Bindung ans Brett ist e.target hier das Brett selbst – also nachsehen, was unter dem Zeiger liegt
    const id = elementUnter(document.elementFromPoint(e.clientX, e.clientY));
    const el = id ? sync.get(id) : undefined;
    if (el && (el.art === 'text' || el.art === 'notiz' || istFlaeche(el.art))) return editorOeffnen(el, false);
    if (!el && werkzeug === 'auswahl') {
      const w = welt(bildschirm(e));
      const neuId = neueId();
      editorOeffnen({ ...wbStandard('text', neuId), x: w.x, y: w.y - stil.schrift * 0.6, farbe: stil.farbe, schrift: stil.schrift, ...wbTextKasten('', stil.schrift), z: naechstesZ() }, true);
    }
  };

  // ---------------------------------------------------------------- Tastatur und Einfügen

  const tastenRef = useRef<(e: KeyboardEvent) => void>(() => {});
  tastenRef.current = (e: KeyboardEvent) => {
    const ziel = e.target as HTMLElement;
    if (ziel?.closest?.('input, textarea, select, [contenteditable], dialog[open]')) return;
    // AltGr meldet sich unter Windows als Strg+Alt – das ist kein Kürzel (z. B. [ und ] auf deutscher Tastatur)
    const strg = (e.ctrlKey || e.metaKey) && !e.altKey;
    const t = e.key.toLowerCase();
    if (e.key === ' ') {
      leertaste.current = true;
      if (ziel === document.body || rahmenRef.current?.contains(ziel)) e.preventDefault();
      return;
    }
    if ((t === '+' || t === '=') && !strg) return zoomUm(1.2);
    if (t === '-' && !strg) return zoomUm(1 / 1.2);
    if (t === '0' && !strg) return setAnsicht({ ...ansichtRef.current, zoom: 1 });
    if (e.key === '!' || (t === '1' && e.shiftKey)) return allesZeigen();
    if (e.key === 'Escape') {
      if (zugRef.current) zugAbbrechen();
      else if (auswahl.size) setAuswahl(new Set());
      else setWerkzeug('auswahl');
      return;
    }
    if (!darfSchreiben) return;
    if (strg && t === 'z') {
      e.preventDefault();
      return e.shiftKey ? sync.wiederholen() : sync.rueckgaengig();
    }
    if (strg && t === 'y') {
      e.preventDefault();
      return sync.wiederholen();
    }
    if (strg && t === 'a') {
      e.preventDefault();
      setWerkzeugRoh('auswahl');
      return setAuswahl(new Set(sichtbar.map((x) => x.id)));
    }
    if (strg && t === 'g') {
      e.preventDefault();
      return gruppieren(!e.shiftKey);
    }
    if (strg && t === 'd') {
      e.preventDefault();
      ablage.current = auswahlEls.map((x) => ({ ...x }));
      einfuegenZahl.current = 0;
      return einfuegen();
    }
    if (strg && t === 'c') return kopieren();
    if (strg && t === 'x') {
      kopieren();
      return loeschen(auswahl);
    }
    if (strg) return;
    if (e.key === 'Delete' || e.key === 'Backspace') return loeschen(auswahl);
    if (e.key === ']' || (e.key === 'PageUp' && e.shiftKey)) return ebene('vorn');
    if (e.key === '[' || (e.key === 'PageDown' && e.shiftKey)) return ebene('hinten');
    if (e.key === 'PageUp') return ebeneEinzeln(1);
    if (e.key === 'PageDown') return ebeneEinzeln(-1);
    if (e.key.startsWith('Arrow') && auswahlEls.length) {
      e.preventDefault();
      const s = e.shiftKey ? 10 : 1;
      const dx = e.key === 'ArrowLeft' ? -s : e.key === 'ArrowRight' ? s : 0;
      const dy = e.key === 'ArrowUp' ? -s : e.key === 'ArrowDown' ? s : 0;
      const m = new Map<string, WbFelder>(auswahlEls.map((x) => [x.id, { x: x.x + dx, y: x.y + dy }]));
      return sync.aendern(alsAenderungen(mitPfeilen(m, new Set(m.keys()))));
    }
    const wz = TASTEN[t];
    if (wz && !e.altKey) setWerkzeug(wz);
  };

  useEffect(() => {
    const runter = (e: KeyboardEvent) => tastenRef.current(e);
    const hoch = (e: KeyboardEvent) => {
      if (e.key === ' ') leertaste.current = false;
    };
    const einf = (e: ClipboardEvent) => {
      const ziel = e.target as HTMLElement;
      if (ziel?.closest?.('input, textarea, [contenteditable]')) return;
      const datei = [...(e.clipboardData?.files ?? [])].find((f) => f.type.startsWith('image/'));
      if (datei) {
        e.preventDefault();
        void bildEinfuegenRef.current(datei);
      } else if (ablage.current.length) {
        e.preventDefault();
        einfuegenRef.current();
      }
    };
    window.addEventListener('keydown', runter);
    window.addEventListener('keyup', hoch);
    window.addEventListener('paste', einf);
    return () => {
      window.removeEventListener('keydown', runter);
      window.removeEventListener('keyup', hoch);
      window.removeEventListener('paste', einf);
    };
  }, []);
  const bildEinfuegenRef = useRef(bildEinfuegen);
  bildEinfuegenRef.current = bildEinfuegen;
  const einfuegenRef = useRef(() => einfuegen());
  einfuegenRef.current = () => einfuegen();

  // ---------------------------------------------------------------- Export und Name

  const exportieren = async (art: 'png' | 'svg') => {
    setExportLaeuft(true);
    try {
      // Mit Auswahl: nur die ausgewählten Elemente, sonst die ganze Tafel
      const nurAuswahl = auswahlEls.length > 0;
      const els = nurAuswahl
        ? wbSortiert(auswahlEls.map((e) => sync.get(e.id)).filter((e): e is WbElement => !!e))
        : wbSortiert(sync.ansicht().values());
      const datei = nurAuswahl ? `${name} (Auswahl)` : name;
      // Dunkle Tafeln werden dunkel exportiert, alle anderen hell (wie gedruckt)
      const dunkel = tafelFarbe === 'dunkel';
      if (art === 'png') await alsPngSpeichern(datei, els, dunkel);
      else await alsSvgSpeichern(datei, els, dunkel);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : 'Export fehlgeschlagen');
    } finally {
      setExportLaeuft(false);
    }
  };

  const nameSpeichern = async () => {
    const n = nameBearbeiten?.trim();
    setNameBearbeiten(null);
    if (!n || n === name) return;
    try {
      await api.patch(modulPfad(projekt.id, 'whiteboard', `tafeln/${tafelId}`), { name: n });
      setName(n);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : 'Umbenennen fehlgeschlagen');
    }
  };

  const farbeSpeichern = async (f: WbTafelFarbe) => {
    const vorher = tafelFarbe;
    setTafelFarbe(f);
    try {
      await api.patch(modulPfad(projekt.id, 'whiteboard', `tafeln/${tafelId}`), { farbe: f });
    } catch (e) {
      setTafelFarbe(vorher);
      setMeldung(e instanceof Error ? e.message : 'Umstellen fehlgeschlagen');
    }
  };

  // ---------------------------------------------------------------- Zeichnen

  if (weg) {
    return (
      <div className="seite">
        <Leer titel="Diese Tafel gibt es nicht (mehr).">
          <Link to="..">Zur Übersicht der Tafeln</Link>
        </Leer>
      </div>
    );
  }

  const v = ansicht;
  const kasten = auswahlKasten(auswahlEls);
  const einzelLinie = auswahlEls.length === 1 && (auswahlEls[0]!.art === 'linie' || auswahlEls[0]!.art === 'pfeil') ? auswahlEls[0]! : null;
  const zeigeGriffe = darfSchreiben && werkzeug === 'auswahl' && !rahmen && kasten && !editor;
  const editEl = editor ? sync.get(editor.id) : undefined;
  const editLage = editEl ? aufSchirm({ x: editEl.x, y: editEl.y }) : null;
  const ausgewaehltArten = new Set(auswahlEls.map((e) => e.art));
  const zeigtFuellung = ['rechteck', 'ellipse', 'raute'].includes(werkzeug) || [...ausgewaehltArten].some(istFlaeche);
  const zeigtNotiz = werkzeug === 'notiz' || ausgewaehltArten.has('notiz');
  const zeigtSchrift =
    werkzeug === 'text' || werkzeug === 'notiz' || ausgewaehltArten.has('text') || ausgewaehltArten.has('notiz') || [...ausgewaehltArten].some(istFlaeche);
  const zeigtStrich = ['stift', 'linie', 'pfeil', 'rechteck', 'ellipse', 'raute'].includes(werkzeug) || [...ausgewaehltArten].some(hatStrich);
  const zeigtFarbe = zeigtStrich || zeigtSchrift;
  const zeigtStil = darfSchreiben && (zeigtFarbe || zeigtFuellung || zeigtNotiz);
  const punktGriff = 9;

  const griffe: { id: string; p: Punkt; cursor: string }[] = [];
  if (zeigeGriffe && kasten) {
    if (einzelLinie) {
      const [a, b] = endpunkteWelt(einzelLinie);
      griffe.push({ id: 'p0', p: aufSchirm(a), cursor: 'move' }, { id: 'p1', p: aufSchirm(b), cursor: 'move' });
    } else {
      const ecken = auswahlEls.length === 1 && auswahlEls[0]!.art === 'text' ? [[-1, -1], [1, -1], [1, 1], [-1, 1]] : [[-1, -1], [0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0]];
      for (const [sx, sy] of ecken as [number, number][]) {
        const winkel = (((Math.atan2(sy, sx) * 180) / Math.PI + kasten.w) % 180 + 180) % 180;
        const cursor = winkel < 22.5 || winkel >= 157.5 ? 'ew-resize' : winkel < 67.5 ? 'nwse-resize' : winkel < 112.5 ? 'ns-resize' : 'nesw-resize';
        griffe.push({ id: `${sx},${sy}`, p: aufSchirm(kastenPunkt(kasten, sx, sy)), cursor });
      }
    }
  }
  const drehGriff = zeigeGriffe && kasten && !einzelLinie ? (() => {
    const oben_ = kastenPunkt(kasten, 0, -1);
    const r = drehen({ x: 0, y: -26 / v.zoom }, kasten.w);
    return aufSchirm({ x: oben_.x + r.x, y: oben_.y + r.y });
  })() : null;
  const kastenEcken = kasten ? ([[-1, -1], [1, -1], [1, 1], [-1, 1]] as const).map(([sx, sy]) => aufSchirm(kastenPunkt(kasten, sx, sy))) : null;

  const cursor =
    !darfSchreiben || werkzeug === 'hand' ? 'grab' : werkzeug === 'auswahl' ? 'default' : werkzeug === 'text' ? 'text' : werkzeug === 'radierer' ? 'cell' : 'crosshair';

  return (
    <div className="wb-seite">
      <header className="wb-kopf">
        <Link to=".." className="knopf knopf-leise knopf-klein" aria-label="Zurück zu den Tafeln">
          {Symbol.pfeilLinks}
        </Link>
        {nameBearbeiten !== null ? (
          <input
            className="eingabe wb-name-eingabe"
            autoFocus
            value={nameBearbeiten}
            maxLength={120}
            onChange={(e) => setNameBearbeiten(e.target.value)}
            onBlur={nameSpeichern}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void nameSpeichern();
              if (e.key === 'Escape') setNameBearbeiten(null);
            }}
          />
        ) : (
          <h2
            className={`wb-name${darfSchreiben ? ' wb-name-aenderbar' : ''}`}
            title={darfSchreiben ? 'Zum Umbenennen klicken' : undefined}
            onClick={() => darfSchreiben && setNameBearbeiten(name)}
          >
            {name || '…'}
          </h2>
        )}
        {!darfSchreiben && <span className="marke">nur ansehen</span>}
        {!verbunden && <span className="marke marke-warn">Verbindung wird aufgebaut …</span>}
        <span className="abstand" />
        <div className="wb-anwesende" aria-label="Gerade auf der Tafel">
          {anwesende
            .filter((a, i, l) => l.findIndex((x) => x.benutzerId === a.benutzerId) === i)
            .map((a) => (
              <span key={a.verbindungId} className="wb-person" style={{ background: a.farbe }} title={a.name}>
                {a.name.slice(0, 1).toUpperCase()}
              </span>
            ))}
        </div>
        {darfSchreiben && (
          <Auswahl
            className="wb-tafelfarbe"
            value={tafelFarbe}
            onChange={(e) => void farbeSpeichern(e.target.value as WbTafelFarbe)}
            title="Hintergrund der Tafel (für alle)"
            aria-label="Hintergrund der Tafel"
          >
            {WB_TAFELFARBEN.map((f) => (
              <option key={f} value={f}>
                {WB_TAFELFARBE_NAMEN[f]}
              </option>
            ))}
          </Auswahl>
        )}
        <Knopf klein className="wb-export" onClick={() => exportieren('png')} disabled={exportLaeuft || !sichtbar.length} title={auswahlEls.length ? 'Auswahl als Bild (PNG) herunterladen' : 'Als Bild (PNG) herunterladen'}>
          PNG
        </Knopf>
        <Knopf klein className="wb-export" onClick={() => exportieren('svg')} disabled={exportLaeuft || !sichtbar.length} title={auswahlEls.length ? 'Auswahl als Vektorgrafik (SVG) herunterladen' : 'Als Vektorgrafik (SVG) herunterladen'}>
          SVG
        </Knopf>
      </header>

      <div
        ref={rahmenRef}
        className={`wb-flaeche wb-farbe-${tafelFarbe}`}
        style={{
          cursor,
          backgroundSize: `${24 * v.zoom}px ${24 * v.zoom}px`,
          backgroundPosition: `${-v.x * v.zoom}px ${-v.y * v.zoom}px`,
        }}
        onDragOver={(e) => darfSchreiben && e.preventDefault()}
        onDrop={(e) => {
          if (!darfSchreiben) return;
          e.preventDefault();
          const w = welt(bildschirm(e));
          for (const f of [...e.dataTransfer.files].filter((f) => f.type.startsWith('image/'))) void bildEinfuegen(f, w);
        }}
      >
        <svg
          className="wb-svg"
          width={groesse.b}
          height={groesse.h}
          onPointerDown={unten}
          onPointerMove={bewegen}
          onPointerUp={oben}
          onPointerCancel={abgebrochen}
          onPointerLeave={() => zeigerSenden(null)}
          onDoubleClick={doppelklick}
          onContextMenu={(e) => e.preventDefault()}
        >
          <g className="wb-inhalt" transform={`scale(${v.zoom}) translate(${-v.x} ${-v.y})`}>
            {sichtbar.map((e) => {
              const f = vorschau?.get(e.id);
              const imEditor = editor?.id === e.id;
              const g = f ? { ...e, ...f } : e;
              return (
                <ElementG
                  key={e.id}
                  e={imEditor && editor.art === 'form' ? { ...g, text: '' } : g}
                  blass={radiert.has(e.id)}
                  versteckt={imEditor && editor.art !== 'form'}
                />
              );
            })}
            {[...fremde.values()].map((f) => f.entwurf && <ElementG key={`f-${f.verbindungId}`} e={f.entwurf} geist />)}
            {entwurf && <ElementG e={entwurf} geist />}
          </g>
          <g className="wb-ueber">
            {kastenEcken && !einzelLinie && (
              <polygon points={kastenEcken.map((p) => `${p.x},${p.y}`).join(' ')} className="wb-auswahl-rahmen" />
            )}
            {auswahlEls.length > 1 &&
              auswahlEls.map((e) => {
                const k = wbHuelle(e);
                const a = aufSchirm({ x: k.x, y: k.y });
                return <rect key={e.id} x={a.x} y={a.y} width={k.b * v.zoom} height={k.h * v.zoom} className="wb-auswahl-einzeln" />;
              })}
            {drehGriff && kastenEcken && (
              <>
                <line x1={(kastenEcken[0]!.x + kastenEcken[1]!.x) / 2} y1={(kastenEcken[0]!.y + kastenEcken[1]!.y) / 2} x2={drehGriff.x} y2={drehGriff.y} className="wb-auswahl-rahmen" />
                <circle data-griff="drehen" cx={drehGriff.x} cy={drehGriff.y} r={6} className="wb-griff" style={{ cursor: 'grab' }} />
              </>
            )}
            {griffe.map((g) => (
              <rect
                key={g.id}
                data-griff={g.id}
                x={g.p.x - punktGriff / 2}
                y={g.p.y - punktGriff / 2}
                width={punktGriff}
                height={punktGriff}
                rx={g.id.startsWith('p') ? punktGriff / 2 : 2}
                className="wb-griff"
                style={{ cursor: g.cursor }}
              />
            ))}
            {halter && sync.get(halter) && (() => {
              const k = wbHuelle(sync.get(halter)!);
              const a = aufSchirm({ x: k.x, y: k.y });
              return <rect x={a.x - 4} y={a.y - 4} width={k.b * v.zoom + 8} height={k.h * v.zoom + 8} rx={6} className="wb-halter" />;
            })()}
            {rahmen && (
              <rect x={(rahmen.x - v.x) * v.zoom} y={(rahmen.y - v.y) * v.zoom} width={rahmen.b * v.zoom} height={rahmen.h * v.zoom} className="wb-rahmen" />
            )}
          </g>
        </svg>

        {[...fremde.values()].map((f) => {
          const s = aufSchirm(f);
          if (s.x < -20 || s.y < -20 || s.x > groesse.b + 20 || s.y > groesse.h + 20) return null;
          return (
            <div key={f.verbindungId} className="wb-fremder-zeiger" style={{ transform: `translate(${s.x}px, ${s.y}px)`, color: f.farbe }}>
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
                <path d="M2 2l13 6-5.5 1.5L7 15z" fill="currentColor" stroke="#fff" strokeWidth="1.2" />
              </svg>
              <span style={{ background: f.farbe }}>{f.name}</span>
            </div>
          );
        })}

        {editor && editEl && editLage && (
          <textarea
            ref={editorRef}
            autoFocus
            onFocus={(e) => e.currentTarget.select()}
            className={`wb-editor wb-editor-${editor.art}`}
            value={editor.text}
            placeholder={editor.art === 'notiz' ? 'Notiz …' : editor.art === 'form' ? 'Beschriftung …' : 'Text …'}
            onChange={(e) => editorTippen(e.target.value)}
            onBlur={editorFertig}
            onKeyDown={(e) => {
              if (e.key === 'Escape' || (e.key === 'Enter' && (e.ctrlKey || e.metaKey))) {
                e.preventDefault();
                editorFertig();
              }
            }}
            style={editor.art === 'form' ? formEditorStil(editEl, editor.text, editLage, v.zoom) : {
              left: editLage.x,
              top: editLage.y,
              width: Math.max(editor.art === 'text' ? 80 : 40, (editor.art === 'text' ? wbTextKasten(editor.text, editEl.schrift).b + editEl.schrift : editEl.b) * v.zoom),
              height: (editor.art === 'text' ? wbTextKasten(editor.text, editEl.schrift).h : editEl.h) * v.zoom,
              fontSize: editEl.schrift * v.zoom,
              lineHeight: 1.25,
              padding: editor.art === 'notiz' ? 12 * v.zoom : 2 * v.zoom,
              color: editor.art === 'notiz' ? editEl.farbe : tinte(editEl.farbe),
              background: editor.art === 'notiz' ? (editEl.fuellung ?? WB_NOTIZ_FARBEN[0]) : 'transparent',
              transform: editEl.drehung ? `rotate(${editEl.drehung}deg)` : undefined,
            }}
          />
        )}

        {darfSchreiben && (
          <div className="wb-werkzeuge" role="toolbar" aria-label="Werkzeuge">
            {WERKZEUGE.map((w) => (
              <button
                key={w.id}
                type="button"
                className={`wb-wz${werkzeug === w.id ? ' wb-wz-an' : ''}`}
                title={`${w.name} (${w.taste})`}
                aria-label={w.name}
                aria-pressed={werkzeug === w.id}
                onClick={() => setWerkzeug(w.id)}
              >
                <Sym>{w.symbol}</Sym>
              </button>
            ))}
            <button type="button" className="wb-wz" title="Bild einfügen (auch per Einfügen oder Hineinziehen)" aria-label="Bild einfügen" onClick={() => dateiEingabe.current?.click()}>
              <Sym>
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <circle cx="9" cy="10" r="1.6" />
                <path d="M21 16l-5-5-8 8" />
              </Sym>
            </button>
            <input
              ref={dateiEingabe}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void bildEinfuegen(f);
                e.target.value = '';
              }}
            />
          </div>
        )}

        {zeigtStil && (
          <div className="wb-stil" aria-label="Aussehen">
            {zeigtFarbe && (
              <div className="wb-stil-zeile" role="group" aria-label="Farbe">
                {WB_FARBEN.map((f) => (
                  <button key={f} type="button" className={`wb-farbe${stil.farbe === f ? ' wb-farbe-an' : ''}`} style={{ background: f }} title="Strich- und Textfarbe" aria-label={`Farbe ${f}`} onClick={() => stilSetzen({ farbe: f })} />
                ))}
              </div>
            )}
            {zeigtFuellung && (
              <div className="wb-stil-zeile" role="group" aria-label="Füllung">
                <button type="button" className={`wb-farbe wb-farbe-keine${stil.fuellung === null ? ' wb-farbe-an' : ''}`} title="Keine Füllung" aria-label="Keine Füllung" onClick={() => stilSetzen({ fuellung: null })} />
                {WB_FUELLUNGEN.map((f) => (
                  <button key={f} type="button" className={`wb-farbe${stil.fuellung === f ? ' wb-farbe-an' : ''}`} style={{ background: f }} title="Füllung" aria-label={`Füllung ${f}`} onClick={() => stilSetzen({ fuellung: f })} />
                ))}
              </div>
            )}
            {zeigtNotiz && (
              <div className="wb-stil-zeile" role="group" aria-label="Notizfarbe">
                {WB_NOTIZ_FARBEN.map((f) => (
                  <button key={f} type="button" className={`wb-farbe${stil.notizFarbe === f ? ' wb-farbe-an' : ''}`} style={{ background: f }} title="Farbe der Haftnotiz" aria-label={`Notizfarbe ${f}`} onClick={() => stilSetzen({ notizFarbe: f })} />
                ))}
              </div>
            )}
            {zeigtStrich && (
              <div className="wb-stil-zeile" role="group" aria-label="Strichstärke">
                {[2, 4, 8].map((s) => (
                  <button key={s} type="button" className={`wb-wahl${stil.staerke === s ? ' wb-wahl-an' : ''}`} title={`Strichstärke ${s}`} onClick={() => stilSetzen({ staerke: s })}>
                    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden>
                      <line x1="4" y1="11" x2="18" y2="11" stroke="currentColor" strokeWidth={s} strokeLinecap="round" />
                    </svg>
                  </button>
                ))}
              </div>
            )}
            {zeigtSchrift && (
              <div className="wb-stil-zeile" role="group" aria-label="Schriftgröße">
                {[
                  [16, 'S'],
                  [24, 'M'],
                  [36, 'L'],
                  [56, 'XL'],
                ].map(([s, t]) => (
                  <button key={s} type="button" className={`wb-wahl${stil.schrift === s ? ' wb-wahl-an' : ''}`} title={`Schriftgröße ${t}`} onClick={() => stilSetzen({ schrift: s as number })}>
                    {t}
                  </button>
                ))}
              </div>
            )}
            {auswahlEls.length > 0 && (
              <div className="wb-stil-zeile" role="group" aria-label="Anordnen">
                <button type="button" className="wb-wahl" title="Ganz nach vorn ( ] oder Umschalt+Bild↑ )" onClick={() => ebene('vorn')}>
                  <Sym><path d="M8 14l4-4 4 4M12 10v10M5 4h14" /></Sym>
                </button>
                <button type="button" className="wb-wahl" title="Eine Ebene nach vorn (Bild↑)" onClick={() => ebeneEinzeln(1)}>
                  <Sym><path d="M8 13l4-4 4 4M12 9v11" /></Sym>
                </button>
                <button type="button" className="wb-wahl" title="Eine Ebene nach hinten (Bild↓)" onClick={() => ebeneEinzeln(-1)}>
                  <Sym><path d="M8 11l4 4 4-4M12 15V4" /></Sym>
                </button>
                <button type="button" className="wb-wahl" title="Ganz nach hinten ( [ oder Umschalt+Bild↓ )" onClick={() => ebene('hinten')}>
                  <Sym><path d="M8 10l4 4 4-4M12 14V4M5 20h14" /></Sym>
                </button>
                {auswahlEls.length > 1 && (
                  <button type="button" className="wb-wahl" title="Gruppieren (Strg+G)" onClick={() => gruppieren(true)}>
                    <Sym><rect x="3" y="3" width="10" height="10" rx="1" /><rect x="11" y="11" width="10" height="10" rx="1" /></Sym>
                  </button>
                )}
                {auswahlEls.some((e) => e.gruppe) && (
                  <button type="button" className="wb-wahl" title="Gruppe auflösen (Strg+Umschalt+G)" onClick={() => gruppieren(false)}>
                    <Sym><rect x="3" y="3" width="8" height="8" rx="1" strokeDasharray="2 2" /><rect x="13" y="13" width="8" height="8" rx="1" strokeDasharray="2 2" /></Sym>
                  </button>
                )}
                <button
                  type="button"
                  className="wb-wahl"
                  title="Verdoppeln (Strg+D)"
                  onClick={() => {
                    ablage.current = auswahlEls.map((x) => ({ ...x }));
                    einfuegenZahl.current = 0;
                    einfuegen();
                  }}
                >
                  <Sym><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" /></Sym>
                </button>
                <button type="button" className="wb-wahl wb-wahl-gefahr" title="Löschen (Entf)" onClick={() => loeschen(auswahl)}>
                  {Symbol.muell}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="wb-unten">
          {darfSchreiben && (
            <div className="wb-gruppe">
              <button type="button" className="wb-wz" title="Rückgängig (Strg+Z)" aria-label="Rückgängig" disabled={!sync.kannRueckgaengig} onClick={() => sync.rueckgaengig()}>
                <Sym><path d="M9 14L4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3" /></Sym>
              </button>
              <button type="button" className="wb-wz" title="Wiederholen (Strg+Y)" aria-label="Wiederholen" disabled={!sync.kannWiederholen} onClick={() => sync.wiederholen()}>
                <Sym><path d="M15 14l5-5-5-5M20 9H10a6 6 0 0 0 0 12h3" /></Sym>
              </button>
            </div>
          )}
          <div className="wb-gruppe">
            <button type="button" className="wb-wz" title="Verkleinern (−)" aria-label="Verkleinern" onClick={() => zoomUm(1 / 1.25)}>
              <Sym><path d="M5 12h14" /></Sym>
            </button>
            <button type="button" className="wb-zoom" title="Auf 100 % (0)" onClick={() => setAnsicht({ ...ansichtRef.current, zoom: 1 })}>
              {Math.round(v.zoom * 100)} %
            </button>
            <button type="button" className="wb-wz" title="Vergrößern (+)" aria-label="Vergrößern" onClick={() => zoomUm(1.25)}>
              <Sym><path d="M12 5v14M5 12h14" /></Sym>
            </button>
            <button type="button" className="wb-wz" title="Alles zeigen (Umschalt+1)" aria-label="Alles zeigen" onClick={() => allesZeigen()}>
              <Sym><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></Sym>
            </button>
          </div>
        </div>

        {!sichtbar.length && sync.geladen && !entwurf && (
          <div className="wb-hinweis">
            {darfSchreiben ? 'Leere Tafel – wähle oben ein Werkzeug und leg los.' : 'Diese Tafel ist noch leer.'}
          </div>
        )}
        {!sync.geladen && <div className="wb-hinweis">Tafel wird geladen …</div>}
        {meldung && (
          <div className="wb-meldung" role="alert">
            <Meldung>
              {meldung}{' '}
              <button type="button" className="knopf knopf-leise knopf-klein" onClick={() => setMeldung('')}>
                OK
              </button>
            </Meldung>
          </div>
        )}
      </div>
    </div>
  );
}
